import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from "@mui/material";

const TermsAndCondition = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTerm, setCurrentTerm] = useState({
    company_id: "",
    company_name: "",
    terms_and_condition_content: "",
    privacy_policy_content: "",
    version: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const token = localStorage.getItem("access_token");

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/terms-and-condtions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch terms and conditions");
      const data = await response.json();
      setTerms(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  const handleEdit = (term) => {
    setCurrentTerm({
      company_id: term.company_id,
      company_name: term.company_name || "",
      terms_and_condition_content: term.terms_and_condition_content || "",
      privacy_policy_content: term.privacy_policy_content || "",
      version: term.version || "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTerm({
      company_id: "",
      company_name: "",
      terms_and_condition_content: "",
      privacy_policy_content: "",
      version: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentTerm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async () => {
    try {
      const updateData = {
        company_name: currentTerm.company_name,
        version: currentTerm.version,
      };

      // Include both contents in the update, but backend will handle which one to update
      updateData.terms_and_condition_content = currentTerm.terms_and_condition_content;
      updateData.privacy_policy_content = currentTerm.privacy_policy_content;

      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/terms-and-condtions?company_id=${currentTerm.company_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update terms and conditions");
      }
      
      setSuccessMessage("Content updated successfully");
      fetchTerms();
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Terms and Conditions / Privacy Policy Management
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Company Name</TableCell>
            <TableCell>Company ID</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {terms.length > 0 ? (
            terms.map((term) => (
              <TableRow key={term.company_id}>
                <TableCell>{term.company_name || "N/A"}</TableCell>
                <TableCell>{term.company_id}</TableCell>
                <TableCell>{term.version || "N/A"}</TableCell>
                <TableCell>
                  {term.updated_at
                    ? new Date(term.updated_at).toLocaleString()
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <Button variant="contained" onClick={() => handleEdit(term)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No terms and conditions found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            // minHeight: "80vh"
            minHeight: "50vh"
          }
        }}
      >
        <DialogTitle>
          Edit Content - {currentTerm.company_name || currentTerm.company_id}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              aria-label="content tabs"
            >
              <Tab label="Terms and Conditions" />
              <Tab label="Privacy Policy" />
            </Tabs>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              margin="dense"
              label="Company Name"
              name="company_name"
              value={currentTerm.company_name}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Version"
              name="version"
              value={currentTerm.version}
              onChange={handleChange}
            />
          </Box>
          
          <TextField
            fullWidth
            margin="dense"
            label="Company ID"
            name="company_id"
            value={currentTerm.company_id}
            disabled
          />
          
          {activeTab === 0 ? (
            <TextField
              fullWidth
              margin="dense"
              label="Terms and Conditions Content"
              name="terms_and_condition_content"
              value={currentTerm.terms_and_condition_content}
              onChange={handleChange}
              multiline
              minRows={8}
              maxRows={20}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          ) : (
            <TextField
              fullWidth
              margin="dense"
              label="Privacy Policy Content"
              name="privacy_policy_content"
              value={currentTerm.privacy_policy_content}
              onChange={handleChange}
              multiline
              minRows={8}
              maxRows={20}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            color="primary"
            disabled={!currentTerm.version || !currentTerm.company_name}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TermsAndCondition;